import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { getAllCustomers } from "../../../services/customerService";
import { Customer } from "../../../types/customer";
import { debug } from "../../../utils/debugLogger";
import { validateOAuthState } from "../../../utils/oauthState";
import {
  handleWordPressCallback,
} from "../platforms/wordpress/services/wordpressAuthentication";
import { disconnectWordPressSite } from "../platforms/wordpress/services/wordpressBlogService";
import type { WordPressConnectedSite } from "../platforms/wordpress/types/wordpress.types";

/**
 * Recover the Cognito token after the WordPress.com OAuth redirect wipes
 * React context. Priority: live AuthContext → wordpress_pending_token in
 * localStorage → generic idToken in localStorage.
 * Mirrors the identical resolveToken helper in LinkedInCallback.tsx.
 */
const resolveToken = (authIdToken: string): string => {
  if (authIdToken) return authIdToken;
  const pending = localStorage.getItem("wordpress_pending_token");
  if (pending) return pending;
  const stored = localStorage.getItem("idToken");
  return stored ?? "";
};

const WordPressCallback = () => {
  const navigate = useNavigate();
  const { idToken: authIdToken, isLoading, isAuthenticated } = useAuth();
  const idToken = resolveToken(authIdToken);

  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customersList, setCustomersList] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [connectedSites, setConnectedSites] = useState<WordPressConnectedSite[] | null>(null);
  const [connectionSaved, setConnectionSaved] = useState(false);
  // When the authorized account has more than one site, we only keep one: the
  // user picks it here and we disconnect the rest. Null until a multi-site
  // account is authorized.
  const [pendingSites, setPendingSites] = useState<WordPressConnectedSite[] | null>(null);
  const [chosenSiteId, setChosenSiteId] = useState<string>("");
  const [isFinalizing, setIsFinalizing] = useState(false);

  // ── Step 1: extract authorization code from URL ───────────────────────────
  useEffect(() => {
    if (isLoading) return;
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = urlParams.get("code");
    const errorParam = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");

    if (!errorParam && !validateOAuthState("wordpress", urlParams.get("state"))) {
      setError("Security check failed (invalid OAuth state). Please try connecting again.");
      return;
    }

    if (errorParam) {
      // Log the raw provider error for debugging, but never surface the raw
      // OAuth code/description (e.g. "access_denied: You need to log in to
      // WordPress.com") to the user. Show a friendly, generic message instead.
      debug.error("WordPress OAuth error:", errorParam, errorDescription);
      setError(
        errorParam === "access_denied"
          ? "WordPress connection was cancelled. You can connect your account whenever you're ready."
          : "We couldn't connect your WordPress account. Please try again."
      );
      return;
    }
    if (!urlCode) {
      setError("No authorization code received. Please try connecting again.");
      return;
    }
    setCode(urlCode);
  }, [isLoading]);

  // ── Step 2: load customers once we have a code and a valid token ──────────
  useEffect(() => {
    if (!idToken || !code) return;
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const customers = await getAllCustomers(idToken);
        setCustomersList(customers);
      } catch (err) {
        debug.error("Error fetching customers:", err);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, [idToken, code]);

  // ── Step 3: user clicks Connect ───────────────────────────────────────────
  const handleConnect = async () => {
    if (!code || !selectedCustomerId) {
      setError("Please select a business profile to continue.");
      return;
    }
    const currentToken = resolveToken(authIdToken);
    if (!currentToken) {
      setError("Please log in first, then connect WordPress.");
      return;
    }
    const redirectUri = `${window.location.origin}/connect/wordpress/callback`;
    setError(null);
    setIsSaving(true);
    try {
      const response = await handleWordPressCallback(
        code,
        selectedCustomerId,
        currentToken,
        redirectUri
      );
      if (!response?.connected_sites) {
        throw new Error("Invalid response from WordPress token exchange");
      }
      localStorage.removeItem("wordpress_pending_token");
      const sites = response.connected_sites;
      if (sites.length > 1) {
        // Multiple sites were connected by the OAuth grant — keep only one.
        // Show a single-select picker; the rest are disconnected on confirm.
        setPendingSites(sites);
        setChosenSiteId(sites[0].site_id);
      } else {
        setConnectedSites(sites);
        setConnectionSaved(true);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to connect WordPress. Please try again.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Keep only the chosen site: disconnect every other site that the OAuth grant
  // connected, then show the success screen with the single kept site.
  const handleFinalizeSelection = async () => {
    if (!pendingSites || !chosenSiteId || !selectedCustomerId) return;
    const currentToken = resolveToken(authIdToken);
    if (!currentToken) {
      setError("Please log in first, then connect WordPress.");
      return;
    }
    setError(null);
    setIsFinalizing(true);
    try {
      const toRemove = pendingSites.filter((s) => s.site_id !== chosenSiteId);
      await Promise.all(
        toRemove.map((s) =>
          disconnectWordPressSite(selectedCustomerId, s.site_id, currentToken)
        )
      );
      const chosen = pendingSites.find((s) => s.site_id === chosenSiteId);
      setConnectedSites(chosen ? [chosen] : []);
      setConnectionSaved(true);
      setPendingSites(null);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to finish connecting. Please try again."
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  const goToConnect = () => navigate("/social-posts?tab=connect");

  // Redirect to login if session is gone and no pending token to recover
  if (!isLoading && !isAuthenticated && !localStorage.getItem("wordpress_pending_token")) {
    sessionStorage.setItem("wordpress_auth_redirect", "true");
    navigate("/login", { state: { returnUrl: "/social-posts?tab=connect" } });
    return null;
  }

  // ── Error / cancelled state (no code received) ───────────────────────────
  if (error && !code) {
    return (
      <div className="flex flex-col min-h-screen p-4 bg-theme-background text-theme-fg">
        <div className="w-full flex justify-start pl-4 mb-2">
          <button
            type="button"
            onClick={goToConnect}
            className="flex items-center gap-2 text-theme-fg-muted hover:text-theme-fg font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="w-full max-w-md bg-theme-surface rounded-2xl shadow-sm border border-theme-border p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-theme-primary/10">
              <svg
                className="h-6 w-6 text-theme-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="mb-1.5 text-lg font-semibold text-theme-fg">
              WordPress not connected
            </h2>
            <p className="mb-6 text-sm text-theme-fg-muted">{error}</p>
            <button
              type="button"
              onClick={goToConnect}
              className="rounded-lg bg-theme-primary px-5 py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              Back to Connections
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Auth loading ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen p-4">
        <div className="w-full flex justify-start pl-4 mb-2">
          <button
            type="button"
            onClick={goToConnect}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        </div>
        <div className="flex flex-col items-center justify-center flex-1">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  // ── Main card ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-screen p-4">
      <div className="w-full flex justify-start pl-4 mb-2">
        <button
          type="button"
          onClick={goToConnect}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>

      <div className="flex flex-col items-center w-full flex-1">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <header className="mb-8 pb-8 border-b border-gray-100">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Connect WordPress.com
            </h1>
            <p className="text-sm text-gray-500">
              Connect your WordPress.com account to a business profile.
            </p>
          </header>

          {/* ── Success: show connected sites ─────────────────────────────── */}
          {connectionSaved && connectedSites ? (
            <>
              <div className="mb-6">
                {connectedSites.length > 0 ? (
                  <>
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Connected sites
                    </p>
                    <div className="space-y-3">
                      {connectedSites.map((site) => (
                        <div
                          key={site.site_id}
                          className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">{site.name}</p>
                            {site.url && (
                              <a
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-brand-primary hover:underline break-all"
                              >
                                {site.url}
                              </a>
                            )}
                            <p className="text-sm text-green-700 font-medium mt-1">
                              Connected
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">
                    No WordPress.com sites found on this account.
                  </p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={goToConnect}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
                >
                  Done
                </button>
              </div>
            </>
          ) : pendingSites ? (
            /* ── Multiple sites: pick exactly one to keep connected ──────── */
            <>
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-700">
                  Choose one site to connect
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Your WordPress.com account has multiple sites. Select the one
                  you want to connect — only this site will be connected.
                </p>
              </div>
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              <div className="max-w-xl mb-6 space-y-2">
                {pendingSites.map((site) => (
                  <label
                    key={site.site_id}
                    className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                      chosenSiteId === site.site_id
                        ? "border-theme-primary bg-theme-primary/5"
                        : "border-gray-200 hover:border-theme-primary/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="wordpress-site"
                      value={site.site_id}
                      checked={chosenSiteId === site.site_id}
                      onChange={() => setChosenSiteId(site.site_id)}
                      disabled={isFinalizing}
                      className="mt-1 accent-theme-primary"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{site.name}</p>
                      {site.url && (
                        <span className="text-xs text-gray-500 break-all">
                          {site.url}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleFinalizeSelection}
                  disabled={!chosenSiteId || isFinalizing}
                  className="px-4 py-2 rounded-lg bg-theme-primary text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isFinalizing ? (
                    <span className="flex items-center justify-center gap-1">
                      <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                      Connecting…
                    </span>
                  ) : (
                    "Connect this site"
                  )}
                </button>
              </div>
            </>
          ) : code ? (
            /* ── Code received: let user pick a business profile ─────────── */
            <>
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-700">
                  WordPress.com account authorized
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Select a business profile to connect your WordPress.com sites to.
                </p>
              </div>
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              <div className="max-w-xl mb-6">
                <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-brand-primary transition-colors shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      WordPress.com account
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      You'll choose a single site to connect in the next step.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                        Assign Profile
                      </label>
                      <select
                        value={selectedCustomerId}
                        onChange={(e) => setSelectedCustomerId(e.target.value)}
                        disabled={customersLoading}
                        className="block w-full rounded-lg border border-gray-200 text-xs h-9 px-3 focus:ring-brand-primary focus:border-brand-primary"
                      >
                        <option value="">-- Select a Business Profile --</option>
                        {customersList.map((c) => (
                          <option key={c.customer_id} value={c.customer_id}>
                            {c.business_name}
                            {c.city && c.state ? ` - ${c.city}, ${c.state}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleConnect}
                      disabled={!selectedCustomerId || isSaving}
                      className="w-full py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                      {isSaving ? (
                        <span className="flex items-center justify-center gap-1">
                          <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                          Connecting...
                        </span>
                      ) : (
                        "Connect"
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={goToConnect}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
                >
                  Done
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WordPressCallback;
